import '@xo-union/tk-ui-typography/lib/globals.css';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import get from 'lodash/get';
import BulkSelectToolbar from '~/glm/components/guest-list/bulk-select-toolbar';
import PageHeaderIA from '~/glm/components/shared/layout/page-header-ia';
import { IconButton } from '@xo-union/tk-component-icons';
import { H4 } from '@xo-union/tk-ui-typography';
import { EventModal, ScheduleOfEventsModal } from '../../../../libs/component-event-modal';
import StatsContainer from '~/glm/components/guest-list/header/stats-container';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import AnalyticsStore from '~/glm/models/analytics-store';
import GuestSearchField from '~/glm/components/guest-list/header/guest-search-field';
import FilterGuestDropDown from '~/glm/components/guest-list/header/filter-guests-dropdown';
import SelectedGuestsDropdowns from '~/glm/components/guest-list/header/selected-guests-dropdown';
import Card from '~/glm/components/guest-list/cards/card';
import EventFirstTimeMessage from '~/glm/components/guest-list/cards/event-first-time-message.jsx';
import GdsHouseholdCollection from '~/glm/models/gds/gds-household-collection';
import EventCollection from '~/glm/models/gds/event-collection';
import GuestListStore from '~/glm/models/guest-list-store';
import UserStore from '~/glm/models/user-store';
import AddGuestButton from '~/glm/components/guest-list/header/add-guest-button';
import DownloadListButton from '~/glm/components/guest-list/header/download-list-button';
import styles from './header.scss';
import ZeroState from '~/glm/components/zero-state';

@observer
class SelectedEventHeader extends Component {
  closeModal = () => {
    const { gdsUiState } = this.props;
    gdsUiState.setShowEventModal(false);
  };

  static propTypes = {
    gdsUiState: PropTypes.instanceOf(GdsUiState).isRequired,
    analyticsStore: PropTypes.instanceOf(AnalyticsStore).isRequired,
    gdsHouseholds: PropTypes.instanceOf(GdsHouseholdCollection),
    gdsEvents: PropTypes.instanceOf(EventCollection).isRequired,
    guestListStore: PropTypes.instanceOf(GuestListStore).isRequired,
    userStore: PropTypes.instanceOf(UserStore).isRequired,
  };

  filterRsvps = filter => {
    const { gdsUiState, analyticsStore } = this.props;
    let rsvpStatus;
    if (gdsUiState.rsvpFilter !== filter) rsvpStatus = filter;
    gdsUiState.setRsvpFilter(rsvpStatus, gdsUiState.selectedEvent);
    analyticsStore.track('Dashboard Interaction', { action: 'Filter by RSVP' });
  };

  filterMeals = meal => {
    const { gdsUiState, analyticsStore } = this.props;
    const filter = get(meal, ['text']) || 'No Reply';
    let mealStatus;

    if (gdsUiState.mealFilter !== filter) mealStatus = filter;
    gdsUiState.setMealFilter(mealStatus);
    analyticsStore.track('Dashboard Interaction', { action: 'Filter by Meal' });
  };

  handleClick = () => {
    const { gdsUiState } = this.props;
    gdsUiState.setShowEventModal(true);
    this.props.analyticsStore.track('Dashboard Interaction', {
      action: 'event update initiated',
    });
  };

  onSuccess = ({ questions = [], ...updatedEvent }) => {
    try {
      const { gdsUiState, gdsEvents, gdsHouseholds } = this.props;
      const selectedEvent = gdsEvents.get(updatedEvent.id);

      if (updatedEvent.deleted) {
        this.closeModal();
        gdsUiState.setSelectedEvent(gdsEvents.weddingEvent);
        gdsEvents.remove(selectedEvent);
        gdsHouseholds.removeInvitations(updatedEvent.id);
        return;
      }

      const params = {
        ...updatedEvent,
        fullAddress: { ...updatedEvent.location },
        locationName: get(updatedEvent, ['location', 'name']),
      };

      selectedEvent.setProps(params);
      selectedEvent.setCustomQuestions(questions);
      this.removeInvalidMealAnswers(selectedEvent);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  removeInvalidMealAnswers = selectedEvent => {
    const { gdsHouseholds } = this.props;

    gdsHouseholds.forEach(household => {
      household.members.forEach(member => {
        member.invitations.forEach(invitation => {
          // Only remove invalid meal answers on the relevant event
          // (the event that was just changed)
          if (invitation.eventId !== selectedEvent.id) {
            return;
          }

          const mealQuestion = selectedEvent.mealQuestion;
          if (!mealQuestion.options || mealQuestion.options.length === 0) {
            // Remove all answers to this meal question
            // as there are no longer any options
            invitation.removeAnswer(mealQuestion.id);
            return;
          }

          // Find specific options that were deleted
          invitation.answers.forEach(answer => {
            // Only remove invalid meal answers
            if (answer.questionId !== mealQuestion.id) {
              return;
            }
            if (!mealQuestion.options.find(option => option.id === answer.optionId)) {
              invitation.removeAnswer(mealQuestion.id);
            }
          });
        });
      });
    });
  };

  clearFilters = () => {
    const { gdsUiState, analyticsStore } = this.props;

    analyticsStore.track('Dashboard Interaction', {
      action: 'clear results',
    });

    gdsUiState.setRsvpFilter('', null);
    gdsUiState.setAddressFilter(false);
    gdsUiState.setMealFilter('');
    gdsUiState.setFilter('');
  };

  showSubHeader = () => {
    const { gdsUiState } = this.props;

    return !gdsUiState.isBulkSelecting && !this.activeFilter && this.event.invitedHouseholds.length > 0;
  };

  showDropdownAndSearchBar = () => {
    const { gdsUiState } = this.props;

    return !gdsUiState.isBulkSelecting && this.event.invitedHouseholds.length > 0;
  };

  get event() {
    return this.props.gdsUiState.selectedEvent;
  }

  get activeFilter() {
    const { gdsUiState } = this.props;
    const { rsvpFilter, mealFilter, filter, addressFilter } = gdsUiState;

    return !!(rsvpFilter || mealFilter || addressFilter || filter);
  }

  onHideDate = hideDate => {
    this.props.userStore.updateHideDate(hideDate);
  };

  renderResultClear = () => {
    const { gdsUiState } = this.props;
    if (this.activeFilter) {
      const singular = gdsUiState.filterResultsLength === 1;
      return (
        <div className={styles.filterHeading}>
          <span>
            {gdsUiState.filterResultsLength} RESULT{singular ? '' : 'S'}{' '}
          </span>
          <span onClick={this.clearFilters} className={styles.clear}>
            clear
          </span>
        </div>
      );
    }
  };

  showZeroState = () => {
    return !(this.event && this.event.invitedHouseholds && this.event.invitedHouseholds.length);
  };

  heading = () => {
    const { gdsUiState, analyticsStore, gdsHouseholds } = this.props;

    const showZeroState = this.showZeroState();

    if (this.activeFilter) {
      return this.renderResultClear();
    }

    if (!this.event.invitedHouseholds.length) {
      if (showZeroState) {
        return <ZeroState gdsUiState={gdsUiState} gdsHouseholds={gdsHouseholds} />;
      }
      return (
        <Card>
          <EventFirstTimeMessage
            gdsUiState={gdsUiState}
            gdsHouseholds={gdsHouseholds}
            analyticsStore={analyticsStore}
          />
        </Card>
      );
    }

    const showSubHeader = !gdsUiState.isBulkSelecting && !this.activeFilter && this.event.invitedHouseholds.length > 0;
    if (!showSubHeader) return null;
    return (
      <>
        <div className={styles.eventHeading}>
          <H4 as="h2">{this.event.name}</H4>
          <IconButton
            id="glm-first-visit-2"
            name="edit"
            size="sm"
            className={styles.editButton}
            onClick={this.handleClick}
            aria-label="Edit event"
          />
        </div>
        <div className={styles.subHeadingContainer}>
          <div className={styles.subHeadingCounts}>
            {this.event.guestCount}
            {this.event.guestCount > 1 ? ' Guests' : ' Guest'} Invited:
          </div>
          <StatsContainer
            guestCount={this.event.guestCount}
            acceptedCount={this.event.acceptedCount}
            declinedCount={this.event.declinedCount}
            noResponseCount={this.event.noResponseCount}
            rsvpFilter={this.filterRsvps}
            activeFilter={gdsUiState.rsvpFilter}
          />
        </div>
      </>
    );
  };

  subheading = () => {
    const { gdsUiState } = this.props;
    if (!this.event.invitedHouseholds.length) return null;

    if (gdsUiState.isBulkSelecting) {
      const { selectedEvent } = gdsUiState;
      return (
        <div className={styles.headerTools}>
          <BulkSelectToolbar
            dropdownComponent={updateGuests => (
              <SelectedGuestsDropdowns
                gdsUiState={gdsUiState}
                mealQuestion={selectedEvent.mealQuestion}
                handleClick={updateGuests}
              />
            )}
          />
        </div>
      );
    }

    return (
      <div className={styles.headerTools}>
        {this.showDropdownAndSearchBar() && (
          <>
            <GuestSearchField />
            <FilterGuestDropDown
              rsvpFilter={this.filterRsvps}
              mealFilter={this.filterMeals}
              event={this.event}
              label="Filter By"
            />
          </>
        )}
      </div>
    );
  };

  addGuestDownloadList = () => {
    const { gdsUiState, analyticsStore } = this.props;
    const shouldShow = !gdsUiState.isBulkSelecting && !this.activeFilter && this.event.invitedHouseholds.length > 0;
    if (!shouldShow) return null;

    return (
      <div className={styles.leftAlign}>
        <DownloadListButton />
        <AddGuestButton analyticsStore={analyticsStore} gdsUiState={gdsUiState} />
      </div>
    );
  };

  render() {
    if (!this.event) return null;

    const { gdsUiState, guestListStore, userStore, gdsEvents } = this.props;
    const { selectedEvent } = gdsUiState;
    const {
      customQuestionCollection,
      rsvpErrors,
      dirty,
      placeholderHousehold,
      locationName,
      fullAddress,
      ...rest
    } = selectedEvent;
    let event = {
      ...rest,
      questions: selectedEvent.getCustomQuestions(),
    };
    const numOfHouseholds = this.event.invitedHouseholds.length;

    if (gdsUiState.selectedEvent.type === 'Ceremony') {
      event = [event, gdsEvents.receptionEvent];
    }

    const showingFirstTimeCard = !numOfHouseholds;

    return (
      <Fragment>
        <PageHeaderIA
          heading={this.heading()}
          subHeading={this.subheading()}
          event={this.event}
          search={this.activeFilter}
          firstTimeCard={showingFirstTimeCard}
          showZeroState={this.showZeroState()}
        >
          {this.addGuestDownloadList()}
        </PageHeaderIA>
        {guestListStore &&
          guestListStore.guestWedding &&
          (guestListStore.guestWedding.usesSubEvents ? (
            <ScheduleOfEventsModal
              usesQuestions={guestListStore.guestWedding.usesQuestions}
              open={gdsUiState.showEventModal}
              setClose={this.closeModal}
              weddingId={guestListStore.guestWedding.id}
              event={event}
              hasWWS={userStore.hasWWS}
              type={gdsUiState.selectedEvent.type}
              onSuccess={this.onSuccess}
              hideDate={userStore.hideDate}
              onHideDate={this.onHideDate}
            />
          ) : (
            <EventModal
              usesQuestions={guestListStore.guestWedding.usesQuestions}
              open={gdsUiState.showEventModal}
              setClose={this.closeModal}
              weddingId={guestListStore.guestWedding.id}
              event={event}
              hasWWS={userStore.hasWWS}
              type={gdsUiState.selectedEvent.type}
              onSuccess={this.onSuccess}
              hideDate={userStore.hideDate}
              onHideDate={this.onHideDate}
            />
          ))}
      </Fragment>
    );
  }
}

export default SelectedEventHeader;
